/**
 * Signup timing — captures the "started at" timestamp for each tracked flow
 * in sessionStorage so we can compute total + per-step elapsed durations for
 * Plausible events.
 *
 * Why sessionStorage and not in-memory state: the flow spans multiple component
 * mounts (and survives refreshes within the same tab), so an in-memory ref
 * would reset and report bogus durations. sessionStorage is tab-scoped so a
 * second tab gets its own clock and a closed-then-reopened browser tomorrow
 * starts fresh.
 *
 * Plausible can't natively chart raw numeric durations on custom events with
 * useful breakdowns, so every duration is also bucketed into a small finite
 * categorical so the dashboard "Properties" view groups well.
 */

const STORAGE = typeof window !== 'undefined' ? window.sessionStorage : undefined;

const KEYS = {
  signupStartedAt: 'gh_signup_started_at',
  weeklyConsentStartedAt: 'gh_weekly_consent_started_at',
} as const;

export type DurationBucket =
  | '<10s'
  | '10-30s'
  | '30-60s'
  | '60-120s'
  | '120-300s'
  | '>300s';

export function bucketSeconds(seconds: number): DurationBucket {
  if (seconds < 10) return '<10s';
  if (seconds < 30) return '10-30s';
  if (seconds < 60) return '30-60s';
  if (seconds < 120) return '60-120s';
  if (seconds < 300) return '120-300s';
  return '>300s';
}

function readStart(key: string): number | null {
  if (!STORAGE) return null;
  const raw = STORAGE.getItem(key);
  if (!raw) return null;
  const ms = Number(raw);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return ms;
}

function writeStart(key: string): void {
  STORAGE?.setItem(key, String(Date.now()));
}

function clearStart(key: string): void {
  STORAGE?.removeItem(key);
}

/**
 * Ensure a signup clock is running. Idempotent — if a start is already
 * recorded (mid-flow refresh, back-navigation), the existing timestamp is
 * preserved so the total flow duration we report on the terminal Signup
 * event reflects the user's actual end-to-end experience.
 */
export function startSignupClock(): void {
  if (readStart(KEYS.signupStartedAt) === null) writeStart(KEYS.signupStartedAt);
}

export function elapsedSignupSeconds(): number {
  const start = readStart(KEYS.signupStartedAt);
  if (start === null) return 0;
  return Math.max(0, Math.round((Date.now() - start) / 1000));
}

export function endSignupClock(): void {
  clearStart(KEYS.signupStartedAt);
}

export function startWeeklyConsentClock(): void {
  if (readStart(KEYS.weeklyConsentStartedAt) === null) writeStart(KEYS.weeklyConsentStartedAt);
}

export function elapsedWeeklyConsentSeconds(): number {
  const start = readStart(KEYS.weeklyConsentStartedAt);
  if (start === null) return 0;
  return Math.max(0, Math.round((Date.now() - start) / 1000));
}

export function endWeeklyConsentClock(): void {
  clearStart(KEYS.weeklyConsentStartedAt);
}
